/**
 * Node category types
 */
export type NodeCategory = 'it_system' | 'document' | 'data' | 'custom'

/**
 * Collection status for infrastructure nodes
 */
export type InfraCollectionStatus =
  | 'not_applicable'
  | 'pending'
  | 'sent'
  | 'in_progress'
  | 'returned'
  | 'ingested'
  | 'issues'

/**
 * Endpoint import source types
 */
export type EndpointImportSource = 'manual' | 'csv' | 'json' | 'questionnaire' | 'artifact_derived'

/**
 * Criticality levels
 */
export type CriticalityLevel = 'critical' | 'high' | 'medium' | 'low' | 'unknown'

/**
 * Importance levels (priority for collection/analysis focus)
 */
export type ImportanceLevel = 'critical' | 'high' | 'medium' | 'low' | 'unknown'

/**
 * Compromise status
 */
export type CompromiseStatus = 'confirmed' | 'suspected' | 'cleared' | 'unknown'

/**
 * Predefined IT system roles
 */
export type InfrastructureRole =
  | 'ad'
  | 'dns'
  | 'dhcp'
  | 'proxy'
  | 'file_server'
  | 'web_server'
  | 'database'
  | 'mail'
  | 'print'
  | 'backup'
  | 'monitoring'
  | 'vpn'
  | 'firewall'
  | 'antivirus'
  | 'patching'
  | 'virtualisation'
  | 'container_host'
  | 'jump_box'
  | 'ntp'
  | 'radius'

/**
 * Display labels for predefined infrastructure roles
 */
export const INFRASTRUCTURE_ROLE_LABELS: Record<InfrastructureRole, string> = {
  ad: 'Active Directory',
  dns: 'DNS Server',
  dhcp: 'DHCP Server',
  proxy: 'Proxy Server',
  file_server: 'File Server',
  web_server: 'Web Server',
  database: 'Database Server',
  mail: 'Mail Server',
  print: 'Print Server',
  backup: 'Backup Server',
  monitoring: 'Monitoring/SIEM',
  vpn: 'VPN Gateway',
  firewall: 'Firewall',
  antivirus: 'Antivirus/EDR',
  patching: 'Patch Management',
  virtualisation: 'Hypervisor Host',
  container_host: 'Container Host',
  jump_box: 'Jump Box',
  ntp: 'NTP Server',
  radius: 'RADIUS Server',
}

/**
 * Get display label for a role (supports custom roles)
 */
export function getRoleDisplayLabel(role: string): string {
  return (INFRASTRUCTURE_ROLE_LABELS as Record<string, string | undefined>)[role] ?? role
}

/**
 * Infrastructure node (endpoint) - extended for all entity types
 */
export interface CaseEndpoint {
  id: string
  caseId: string
  name: string
  hostname: string
  ipAddresses: string[] | null
  nodeCategory: NodeCategory
  deviceType: string
  customTypeName: string | null
  operatingSystem: string | null
  osVersion: string | null
  domain: string | null
  ouPath: string | null
  location: string | null
  department: string | null
  environment: string | null
  residesOnNodeId: string | null
  criticality: CriticalityLevel
  importance: ImportanceLevel
  roles: string[]
  compromiseStatus: CompromiseStatus | null
  firstSeenAt: string | null
  lastSeenAt: string | null
  collectionStatus: InfraCollectionStatus
  linkedEndpointId: string | null
  linkedCollectionId: string | null
  hasOpenQuestion: boolean
  openQuestionCount: number
  positionX: number
  positionY: number
  isExpanded: boolean
  importedFrom: string | null
  importSource: EndpointImportSource
  notes: string | null
  isActive: boolean
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

export interface CreateNodeRequest {
  name: string
  hostname?: string
  ipAddresses?: string[]
  nodeCategory: NodeCategory
  deviceType: string
  customTypeName?: string
  operatingSystem?: string
  osVersion?: string
  domain?: string
  ouPath?: string
  location?: string
  department?: string
  environment?: string
  residesOnNodeId?: string
  criticality?: CriticalityLevel
  importance?: ImportanceLevel
  roles?: string[]
  notes?: string
}

export interface CreateEndpointRequest {
  hostname: string
  ipAddresses?: string[]
  deviceType: string
  operatingSystem?: string
  osVersion?: string
  domain?: string
  ouPath?: string
  roles?: string[]
  notes?: string
}

export interface UpdateNodeRequest {
  name?: string
  hostname?: string
  ipAddresses?: string[]
  deviceType?: string
  customTypeName?: string
  operatingSystem?: string
  osVersion?: string
  domain?: string
  ouPath?: string
  location?: string
  department?: string
  environment?: string
  residesOnNodeId?: string
  criticality?: CriticalityLevel
  importance?: ImportanceLevel
  roles?: string[]
  compromiseStatus?: CompromiseStatus
  firstSeenAt?: string
  lastSeenAt?: string
  collectionStatus?: InfraCollectionStatus
  notes?: string
  isActive?: boolean
  isExpanded?: boolean
}

export interface UpdateEndpointRequest {
  hostname?: string
  ipAddresses?: string[]
  deviceType?: string
  operatingSystem?: string
  osVersion?: string
  domain?: string
  ouPath?: string
  importance?: ImportanceLevel
  roles?: string[]
  notes?: string
  isActive?: boolean
}

export interface UpdatePositionRequest {
  positionX: number
  positionY: number
}

export interface LinkCollectionRequest {
  collectionId: string
  collectionStatus: InfraCollectionStatus
}

export interface LinkEndpointRequest {
  endpointId: string
}

export interface ImportEndpointItem {
  hostname: string
  ipAddresses?: string[]
  deviceType: string
  operatingSystem?: string
  osVersion?: string
  domain?: string
  ouPath?: string
  location?: string
  department?: string
  criticality?: CriticalityLevel
  roles?: string[]
  notes?: string
}

export interface ImportEndpointsRequest {
  source: string
  endpoints: ImportEndpointItem[]
}

export interface EndpointImportError {
  row: number
  field: string
  message: string
}

export interface EndpointImportResult {
  imported: number
  skipped: number
  errors: EndpointImportError[]
}

export interface ListEndpointsQuery {
  nodeCategory?: NodeCategory
  deviceType?: string
  domain?: string
  search?: string
  isActive?: boolean
  collectionStatus?: InfraCollectionStatus
  criticality?: CriticalityLevel
  importance?: ImportanceLevel
  hasOpenQuestion?: boolean
  importSource?: EndpointImportSource
}

export interface CollectionStatusSummary {
  status: InfraCollectionStatus
  count: number
}

export interface CustomNodeType {
  id: string
  organisationId: string
  caseId: string | null
  name: string
  category: NodeCategory
  iconName: string | null
  colour: string | null
  description: string | null
  createdBy: string | null
  createdAt: string
}

export interface CreateCustomNodeTypeRequest {
  name: string
  category: NodeCategory
  iconName?: string
  colour?: string
  description?: string
}
